import {DOCUMENT} from '@angular/common';
import {afterNextRender, DestroyRef, inject, Injectable, Injector, NgZone} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router} from '@angular/router';
import {BehaviorSubject, Subscription} from 'rxjs';
import {OnboardingGuide, OnboardingTask} from '@src/app/core/models/onboarding-guide.model';
import {OnboardingNavigationPlan, OnboardingNavigationTarget, onboardingNavigationPlan} from '@src/app/core/models/onboarding-navigation.model';
import {AuthService} from './auth.service';
import {OnboardingGuideService} from './onboarding-guide.service';

export interface OnboardingNavigationState {
    title: string;
    instruction: string;
    ready: boolean;
    final: boolean;
    unavailable: boolean;
    inDialog: boolean;
    step: number;
}

@Injectable({providedIn: 'root'})
export class OnboardingNavigationService {
    private readonly document = inject(DOCUMENT);
    private readonly router = inject(Router);
    private readonly auth = inject(AuthService);
    private readonly guideService = inject(OnboardingGuideService);
    private readonly zone = inject(NgZone);
    private readonly injector = inject(Injector);
    private readonly state = new BehaviorSubject<OnboardingNavigationState | null>(null);
    public readonly state$ = this.state.asObservable();
    public get active(): boolean { return this.state.value !== null; }
    public get targetElement(): HTMLElement | undefined { return this.highlighted; }
    private plan?: OnboardingNavigationPlan;
    private current = 'sites';
    private context = '';
    private step = 1;
    private highlighted?: HTMLElement;
    private originalDescription: string | null = null;
    private target?: OnboardingNavigationTarget;
    private origin?: HTMLElement;
    private observer?: MutationObserver;
    private guideSubscription?: Subscription;
    private timeout?: ReturnType<typeof setTimeout>;
    private navigating = false;

    public constructor() {
        this.router.events.pipe(takeUntilDestroyed()).subscribe(event => {
            if (!this.active) return;
            if (this.context !== this.authContext()) { this.stop(); return; }
            if (event instanceof NavigationStart) { this.navigating = true; this.clearHighlight(); }
            if (event instanceof NavigationEnd) {
                this.navigating = false;
                if (!/^\/app\/(sites|buildings|floors|drawings|reports)(?:\/|\?|$)/.test(event.urlAfterRedirects)) { this.stop(); return; }
                this.refresh();
            }
            if (event instanceof NavigationCancel || event instanceof NavigationError) { this.navigating = false; this.refresh(); }
        });
        inject(DestroyRef).onDestroy(() => this.stop(false));
    }

    public start(task: OnboardingTask, guide: OnboardingGuide): void {
        this.stop(false);
        if (`${guide.tenant_id}:${guide.user_id}` !== this.authContext() || !guide.tasks.some(item => item.key === task.key)) return;
        this.context = this.authContext();
        this.plan = onboardingNavigationPlan(task, guide);
        this.current = this.plan.initialStep;
        this.step = 1;
        this.navigating = this.router.url?.split('?')[0] !== this.plan.destination;
        this.origin = this.document.activeElement as HTMLElement;
        this.document.addEventListener('click', this.onClick, true);
        this.document.addEventListener('input', this.onInput, true);
        this.document.addEventListener('keydown', this.onKeydown, true);
        this.document.defaultView?.addEventListener('resize', this.onResize);
        this.zone.runOutsideAngular(() => {
            this.observer = new MutationObserver(() => this.refresh());
            this.observer.observe(this.document.body, {childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'hidden', 'style', 'class']});
        });
        this.showStep();
        const subscription = this.guideService.guide$.subscribe(next => {
            if (next && (`${next.tenant_id}:${next.user_id}` !== this.context || next.site_id !== guide.site_id || !next.tasks.some(item => item.key === task.key))) this.stop();
        });
        if (this.active) {
            this.guideSubscription = subscription;
            if (this.navigating) this.openDestination();
        } else subscription.unsubscribe();
    }

    private openDestination(): void {
        const plan = this.plan;
        if (!plan) return;
        void this.router.navigateByUrl(plan.destination).then(success => {
            if (this.plan !== plan) return;
            this.navigating = false;
            if (success) this.refresh();
            else this.state.next({...this.state.value, unavailable: true});
        }).catch(() => {
            if (this.plan === plan) this.state.next({...this.state.value, unavailable: true});
        });
    }

    public focusTarget(): void {
        if (!this.highlighted) return;
        this.highlighted.focus({preventScroll: true});
        this.highlighted.scrollIntoView?.({block: 'start', inline: 'nearest', behavior: 'instant'});
    }

    public retry(): void {
        if (!this.active) return;
        if (this.current === this.plan?.initialStep && this.router.url?.split('?')[0] !== this.plan.destination) this.navigating = true;
        this.showStep();
        if (this.navigating) this.openDestination();
    }

    public stop(restoreFocus = true): void {
        const wasActive = this.active;
        const dialogTarget = this.highlighted?.closest('[role="dialog"]') ? this.highlighted : undefined;
        this.observer?.disconnect();
        this.observer = undefined;
        this.guideSubscription?.unsubscribe();
        this.guideSubscription = undefined;
        clearTimeout(this.timeout);
        this.clearHighlight();
        this.document.removeEventListener('click', this.onClick, true);
        this.document.removeEventListener('input', this.onInput, true);
        this.document.removeEventListener('keydown', this.onKeydown, true);
        this.document.defaultView?.removeEventListener('resize', this.onResize);
        this.plan = undefined;
        if (wasActive) this.state.next(null);
        if (wasActive && restoreFocus) {
            const restore = () => {
                if (this.active) return;
                const origin = dialogTarget?.isConnected ? dialogTarget : this.origin?.isConnected && this.origin !== this.document.body ? this.origin : this.document.querySelector<HTMLElement>('[data-onboarding-launcher]');
                origin?.focus();
            };
            restore();
            // The layout reveals the compact launcher after this state change renders.
            afterNextRender(restore, {injector: this.injector});
        }
    }

    private showStep(): void {
        const step = this.plan?.steps[this.current];
        if (!step) return;
        this.clearHighlight();
        clearTimeout(this.timeout);
        this.state.next({title: step.title, instruction: step.waiting, ready: false, final: false, unavailable: false, inDialog: false, step: this.step});
        this.timeout = setTimeout(() => {
            if (this.active && !this.highlighted) this.zone.run(() => this.state.next({...this.state.value, unavailable: true}));
        }, 10000);
        this.refresh();
    }

    private refresh(): void {
        if (!this.plan || this.navigating) return;
        if (this.context !== this.authContext()) { this.zone.run(() => this.stop()); return; }
        const step = this.plan.steps[this.current];
        for (const target of step.targets) {
            const element = Array.from(this.document.querySelectorAll<HTMLElement>(target.selector)).find(item => this.usable(item));
            if (!element) continue;
            if (this.highlighted === element) return;
            this.clearHighlight();
            this.highlighted = element;
            this.target = target;
            element.classList.add('onboarding-navigation-target');
            this.originalDescription = element.getAttribute('aria-describedby');
            element.setAttribute('aria-describedby', [this.originalDescription, 'onboarding-navigation-instruction'].filter(Boolean).join(' '));
            // Keep the user's current keyboard focus; the real control remains operable.
            element.scrollIntoView?.({block: 'start', inline: 'nearest', behavior: 'instant'});
            clearTimeout(this.timeout);
            this.zone.run(() => {
                this.state.next({title: step.title, instruction: target.instruction, ready: true, final: !target.next, unavailable: false, inDialog: !!element.closest('[role="dialog"]'), step: this.step});
                afterNextRender(() => { if (this.highlighted === element) element.focus({preventScroll: true}); }, {injector: this.injector});
            });
            return;
        }
        if (this.highlighted) this.zone.run(() => this.showStep());
    }

    private usable(element: HTMLElement): boolean {
        return !!element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden'
            && !element.closest('[hidden], :disabled, [aria-disabled="true"], [inert]');
    }

    private clearHighlight(): void {
        this.highlighted?.classList.remove('onboarding-navigation-target');
        if (this.highlighted) {
            if (this.originalDescription === null) this.highlighted.removeAttribute('aria-describedby');
            else this.highlighted.setAttribute('aria-describedby', this.originalDescription);
        }
        this.originalDescription = null;
        this.highlighted = undefined;
        this.target = undefined;
    }

    private readonly onClick = (event: MouseEvent): void => {
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        if (!this.highlighted || !(event.target instanceof Node) || !this.highlighted.contains(event.target)) return;
        // Table action menus and selection checkboxes do not open their row.
        if (event.target instanceof Element && event.target.closest('button, input, mat-checkbox') && this.highlighted.tagName === 'TR') return;
        this.zone.run(() => {
            const next = this.target?.next;
            if (!next) { this.stop(false); return; }
            this.current = next;
            this.step++;
            // Wait for Angular to process the real click before finding the next control.
            this.clearHighlight();
            queueMicrotask(() => { if (this.active) this.showStep(); });
        });
    };
    private readonly onInput = (event: Event): void => {
        if (this.highlighted && !this.target?.next && event.target instanceof Node && this.highlighted.contains(event.target)) this.zone.run(() => this.stop(false));
    };
    private readonly onKeydown = (event: KeyboardEvent): void => {
        if (event.key === 'Escape') { event.stopPropagation(); this.zone.run(() => this.stop()); }
    };
    private readonly onResize = (): void => this.refresh();
    private authContext(): string { return `${this.auth.tenant?.id}:${this.auth.user?.id}`; }
}
